// Parties, finance & administration — legacy parity (gap analysis P1–P14).
// customers · suppliers · accounts · incomes · expenses · users · roles ·
// merchants · locations · salaries · halls · tables · brands · categories ·
// units · taxes · fields · customer_groups · modifiers (custom fields only)
import { createElement } from 'react';
import { Book1, Card, Clock, DocumentText, Key, Location as LocationIcon, Map1, Moneys, People, TickCircle, UserAdd, Wallet } from 'iconsax-reactjs';

import { adminApi, fetchSettings } from '../../api/client';
import { isSuperUser } from '../../auth/permissions';
import { t } from '../../i18n';
import { CrudField, CrudRecord, CrudResourceConfig } from '../types';
import { categoriesSummary, moneyValue, relationName, yesNoValue, names } from '../configHelpers';
import { AddressesDialog } from '../../features/parties/AddressesDialog';
import { ImpersonateDialog, UserCardDialog } from '../../features/parties/ImpersonationDialogs';
import { PortalUsersDialog } from '../../features/parties/PortalUsersDialog';
import { UserFilesWidget } from '../../features/parties/UserFilesWidget';
import { startImpersonation } from '../../features/shell/api';
import { approverField, remoteSelect } from '../../features/parties/fields';
import {
  asRuntimeUser,
  currentUserIsSuper,
  dateOnly,
  flag,
  formatAmount,
  hasCurrentUser,
  idList,
  joinNames,
  journalBalance,
  journalBalanceText,
  nullableNumber
} from '../../features/parties/helpers';

type Override = (base: CrudResourceConfig | undefined) => CrudResourceConfig;

function need(base: CrudResourceConfig | undefined, resource: string): CrudResourceConfig {
  if (!base) throw new Error(`Missing base config for ${resource}`);
  return base;
}

const today = () => new Date().toISOString().slice(0, 10);

// --- Shared field builders --------------------------------------------------

const countryField = (section: string, required = false): CrudField => ({
  name: 'country',
  label: 'Country',
  type: 'select',
  optionsEndpoint: 'countries',
  required,
  section
});

const stateField = (section: string, required = false): CrudField => ({
  name: 'state',
  label: 'State',
  type: 'select',
  required,
  section,
  optionsQuery: (form) => (form.country ? `states?country=${encodeURIComponent(String(form.country))}` : null)
});

const addressFields = (section: string, opts: { requireCountry?: boolean } = {}): CrudField[] => [
  { name: 'house_no', label: 'House No', section },
  { name: 'street_no', label: 'Street No', section },
  { name: 'address', label: 'Address', section },
  { name: 'city', label: 'City', section },
  { name: 'postal_code', label: 'Postal Code', section },
  countryField(section, opts.requireCountry),
  stateField(section, opts.requireCountry)
];

const attachmentsField = (section = 'Attachments'): CrudField => ({
  name: 'attachments',
  label: 'Attachments',
  type: 'files',
  accept: '.jpg,.jpeg,.png,.pdf,.zip',
  section,
  width: 'full'
});

const photoField: CrudField = { name: 'photo', label: 'Photo', type: 'file', accept: 'image/*' };

function attachmentCount(row: CrudRecord) {
  return Array.isArray(row.attachments) ? String(row.attachments.length || '') || '-' : '-';
}

function extrasSummary(row: CrudRecord) {
  const extras = row.extra_attributes;
  if (!extras || typeof extras !== 'object' || Array.isArray(extras)) return '-';
  const entries = Object.entries(extras as Record<string, unknown>).filter(([, value]) => value != null && value !== '');
  return entries.length ? entries.map(([key, value]) => `${key}: ${String(value)}`).join(', ') : '-';
}

function pick(payload: Record<string, unknown>, keys: string[]) {
  return keys.reduce<Record<string, unknown>>((next, key) => {
    if (payload[key] !== undefined) next[key] = payload[key] === '' ? null : payload[key];
    return next;
  }, {});
}

// --- Customers / suppliers --------------------------------------------------

function balanceTone(row: CrudRecord) {
  const balance = journalBalance(row);
  if (balance == null || balance === 0) return undefined;
  return balance > 0 ? 'warning' : 'success';
}

function dueAdvance(row: CrudRecord) {
  const balance = journalBalance(row);
  if (balance == null) return '-';
  return balance > 0 ? `${t('parties.due')}: ${formatAmount(balance)}` : `${t('parties.advance')}: ${formatAmount(-balance)}`;
}

function payLink(resource: 'customers' | 'suppliers', row: CrudRecord) {
  const key = resource === 'customers' ? 'customer_id' : 'supplier_id';
  const balance = journalBalance(row);
  // Legacy: pre-fill the amount when the journal balance is negative.
  const amount = balance != null && balance < 0 ? `&amount=${-balance}` : '';
  return `/payments?create=1&${key}=${encodeURIComponent(String(row.id))}${amount}`;
}

const partyKeys = ['name', 'company', 'email', 'phone', 'house_no', 'street_no', 'address', 'city', 'postal_code', 'country', 'state', 'opening_balance', 'due_limit', 'max_due_amount'];

function partyConfig(base: CrudResourceConfig, resource: 'customers' | 'suppliers'): CrudResourceConfig {
  const customers = resource === 'customers';
  return {
    ...base,
    bulkDelete: true,
    customFields: true,
    passthroughQueryParams: ['due_limit'],
    pageSizes: [10, 25, 50, 100],
    columns: [
      { key: 'name', label: 'Name', sortable: true },
      { key: 'company', label: 'Company', sortable: true },
      { key: 'phone', label: 'Phone', sortable: true },
      { key: 'email', label: 'Email', sortable: true },
      ...(customers ? [{ key: 'points', label: 'Points', align: 'right' as const }] : []),
      { key: 'journal', label: 'Balance', align: 'right' as const, render: journalBalanceText, tone: balanceTone },
      ...(customers
        ? [
            { key: 'customer_group', label: 'Customer Group', render: (row: CrudRecord) => relationName(row.customer_group ?? row.customerGroup) },
            { key: 'price_group', label: 'Price Group', render: (row: CrudRecord) => relationName(row.price_group ?? row.priceGroup) }
          ]
        : []),
      { key: 'address', label: 'Address', render: (row) => [row.house_no, row.street_no, row.address, row.city, row.postal_code].filter(Boolean).join(' ') || '-' },
      { key: 'state_name', label: 'State', sortable: true },
      { key: 'country_name', label: 'Country', sortable: true },
      { key: 'extra_attributes', label: 'Custom Fields', render: extrasSummary }
    ],
    detailSections: [
      {
        title: 'Identity',
        fields: [
          { key: 'name', label: 'Name' },
          { key: 'company', label: 'Company' },
          { key: 'email', label: 'Email' },
          { key: 'phone', label: 'Phone' },
          ...(customers
            ? [
                { key: 'customer_group', label: 'Customer Group', render: (row: CrudRecord) => relationName(row.customer_group ?? row.customerGroup) },
                { key: 'price_group', label: 'Price Group', render: (row: CrudRecord) => relationName(row.price_group ?? row.priceGroup) }
              ]
            : [])
        ]
      },
      {
        title: 'Address',
        fields: [
          { key: 'house_no', label: 'House No' },
          { key: 'street_no', label: 'Street No' },
          { key: 'address', label: 'Address' },
          { key: 'city', label: 'City' },
          { key: 'postal_code', label: 'Postal Code' },
          { key: 'state_name', label: 'State' },
          { key: 'country_name', label: 'Country' }
        ]
      },
      {
        title: 'Credit',
        fields: [
          { key: 'opening_balance', label: 'Opening Balance', render: (row) => moneyValue(row.opening_balance) },
          { key: 'due_limit', label: 'Due Limit', render: (row) => moneyValue(row.due_limit) },
          { key: 'max_due_amount', label: 'Max Due Amount', render: (row) => moneyValue(row.max_due_amount) },
          { key: 'journal', label: 'Journal Balance', render: journalBalanceText },
          { key: 'due_advance', label: 'Due / Advance', render: dueAdvance },
          ...(customers ? [{ key: 'points', label: 'Loyalty Points' }] : [])
        ]
      }
    ],
    fields: [
      { name: 'name', label: 'Name', required: true, section: 'Identity' },
      { name: 'company', label: 'Company', section: 'Identity' },
      { name: 'phone', label: 'Phone', section: 'Identity' },
      { name: 'email', label: 'Email', section: 'Identity' },
      { name: 'opening_balance', label: 'Opening Balance', type: 'number', defaultValue: 0, createOnly: true, section: 'Credit' },
      { name: 'due_limit', label: 'Due Limit', type: 'number', section: 'Credit' },
      { name: 'max_due_amount', label: 'Max Due Amount', type: 'number', section: 'Credit' },
      ...(customers
        ? ([
            { name: 'customer_group_id', label: 'Customer Group', type: 'select', optionsEndpoint: 'customer_groups/search', section: 'Credit' },
            { name: 'price_group_id', label: 'Price Group', type: 'select', optionsEndpoint: 'price_groups/search', section: 'Credit' }
          ] as CrudField[])
        : []),
      ...addressFields('Address')
    ],
    normalizeAfterLoad: (record) => ({ ...record, country: record.country ?? '', state: record.state ?? '' }),
    normalizeBeforeSave: (payload) => {
      const next = pick(payload, customers ? [...partyKeys, 'customer_group_id', 'price_group_id'] : partyKeys);
      ['opening_balance', 'due_limit', 'max_due_amount'].forEach((key) => {
        if (key in next) next[key] = nullableNumber(next[key]);
      });
      return next;
    },
    rowActions: [
      {
        key: 'transactions',
        label: 'parties.transactions',
        icon: Book1,
        run: (row, ctx) => ctx.navigate(`/${resource}/${row.id}/transactions`)
      },
      {
        key: 'pay',
        label: 'parties.addPayment',
        icon: Moneys,
        permission: 'create-payments',
        run: (row, ctx) => ctx.navigate(payLink(resource, row))
      },
      {
        key: 'users',
        label: 'parties.portalUsers',
        icon: People,
        permission: `update-${resource}`,
        run: (row, ctx) => {
          ctx.openDialog((close) => createElement(PortalUsersDialog, { resource, partyId: row.id, onClose: close }), { title: `${t('parties.portalUsers')} — ${String(row.name ?? '')}`, maxWidth: 'sm' });
        }
      },
      {
        key: 'add_user',
        label: 'parties.addUser',
        icon: UserAdd,
        permission: `update-${resource}`,
        run: (row, ctx) => {
          ctx.openDialog((close) => createElement(PortalUsersDialog, { resource, partyId: row.id, startWithForm: true, onClose: close }), { title: `${t('parties.portalUsers')} — ${String(row.name ?? '')}`, maxWidth: 'sm' });
        }
      },
      ...(customers
        ? [
            {
              key: 'addresses',
              label: 'parties.addresses',
              icon: LocationIcon,
              permission: 'update-customers',
              run: (row: CrudRecord, ctx: Parameters<NonNullable<CrudResourceConfig['rowActions']>[number]['run']>[1]) => {
                ctx.openDialog((close) => createElement(AddressesDialog, { customerId: row.id, onClose: close }), { title: `${t('parties.addresses')} — ${String(row.name ?? '')}`, maxWidth: 'md' });
              }
            }
          ]
        : [])
    ]
  };
}

// --- Incomes / expenses -----------------------------------------------------

const repeatOptions = ['daily', 'weekly', 'monthly', 'quarterly', 'semiannually', 'annually', 'biennially', 'triennially'].map((value) => ({ label: `parties.repeat.${value}`, value }));

function accountingAfterLoad(record: CrudRecord): CrudRecord {
  const categories = Array.isArray(record.categories) ? (record.categories as Array<{ id?: string }>) : [];
  return {
    ...record,
    date: dateOnly(record.date) ?? today(),
    category_id: record.category_id ?? categories[0]?.id ?? '',
    start_date: dateOnly(record.start_date) ?? '',
    recurring: record.recurring == 1,
    approved_by_id: record.approved_by_id ?? '',
    attachments: []
  };
}

function accountingBeforeSave(payload: Record<string, unknown>, kind: 'income' | 'expense') {
  const editing = Boolean(payload.id);
  const next: Record<string, unknown> = {
    date: payload.date || today(),
    title: payload.title,
    amount: Number(payload.amount ?? 0),
    account_id: payload.account_id,
    category_id: payload.category_id,
    details: payload.details ?? '',
    attachments: payload.attachments
  };
  // The reference column is NOT NULL: blank on create lets the backend
  // generate one; blank on edit keeps the stored reference.
  const reference = String(payload.reference ?? '').trim();
  if (reference || !editing) next.reference = reference;
  // Legacy parity: the Vue forms never send `approved`. Incomes are created
  // with approved=null, which IncomeObserver posts to the account journal;
  // ExpenseController@store derives `approved` from the user's admin role
  // (others go through the Approve action). Sending approved=0 would leave
  // React-created incomes permanently off the books, so the flag is never sent.
  if (kind === 'expense') {
    const recurring = flag(payload.recurring);
    next.approved_by_id = payload.approved_by_id || null;
    next.recurring = recurring;
    if (recurring) {
      next.start_date = payload.start_date || today();
      next.repeat = payload.repeat || 'monthly';
      next.create_before = nullableNumber(payload.create_before);
    }
    if (hasCurrentUser() && !currentUserIsSuper() && !next.approved_by_id) {
      throw new Error(t('parties.approverRequired'));
    }
  }
  return next;
}

function accountingConfig(base: CrudResourceConfig, kind: 'income' | 'expense'): CrudResourceConfig {
  const expense = kind === 'expense';
  return {
    ...base,
    bulkDelete: true,
    customFields: true,
    requiresLocation: true,
    showAttachments: true,
    pageSizes: [10, 25, 50, 100],
    listFilters: expense ? [{ name: 'require_approval', label: 'parties.requireApproval', type: 'toggle', onValue: '1' }] : base.listFilters,
    columns: [
      { key: 'date', label: 'Date', sortable: true },
      { key: 'title', label: 'Title', sortable: true },
      { key: 'reference', label: 'Reference', sortable: true },
      { key: 'amount', label: 'Amount', align: 'right', sortable: true, render: (row) => moneyValue(row.amount) },
      { key: 'attachments', label: 'Attachments', align: 'center', render: attachmentCount },
      { key: 'categories', label: 'Category', render: categoriesSummary },
      { key: 'account', label: 'Account', render: (row) => relationName(row.account) },
      { key: 'user', label: 'Created By', render: (row) => relationName(row.user) },
      ...(expense
        ? [
            { key: 'recurring', label: 'Recurring', render: (row: CrudRecord) => yesNoValue(row.recurring == 1) },
            { key: 'approved', label: 'Approved', render: (row: CrudRecord) => yesNoValue(row.approved == 1), tone: (row: CrudRecord) => (row.approved == 1 ? ('success' as const) : ('warning' as const)) },
            { key: 'approved_by', label: 'Approval', render: (row: CrudRecord) => relationName(row.approved_by ?? row.approvedBy, '') || '-' },
            { key: 'start_date', label: 'Start Date', sortable: true },
            { key: 'repeat', label: 'Repeat', render: (row: CrudRecord) => (row.repeat && row.recurring == 1 ? t(`parties.repeat.${String(row.repeat)}`) : '-') },
            { key: 'create_before', label: 'Before' }
          ]
        : []),
      { key: 'details', label: 'Details' },
      { key: 'extra_attributes', label: 'Custom Fields', render: extrasSummary },
      { key: 'created_at', label: 'Created', sortable: true }
    ],
    detailSections: [
      {
        title: 'Summary',
        fields: [
          { key: 'date', label: 'Date' },
          { key: 'number', label: 'Number' },
          { key: 'reference', label: 'Reference' },
          { key: 'title', label: 'Title' },
          { key: 'amount', label: 'Amount', render: (row) => moneyValue(row.amount) },
          { key: 'account', label: 'Account', render: (row) => relationName(row.account) },
          { key: 'categories', label: 'Category', render: categoriesSummary },
          { key: 'location', label: 'Location', render: (row) => relationName(row.location) },
          { key: 'user', label: 'Created By', render: (row) => relationName(row.user) }
        ]
      },
      ...(expense
        ? [
            {
              title: 'Approval',
              fields: [
                { key: 'approved', label: 'Approved', render: (row: CrudRecord) => yesNoValue(row.approved == 1) },
                { key: 'approved_by', label: 'Approval', render: (row: CrudRecord) => relationName(row.approved_by ?? row.approvedBy) },
                { key: 'recurring', label: 'Recurring', render: (row: CrudRecord) => yesNoValue(row.recurring == 1) },
                { key: 'start_date', label: 'Start Date' },
                { key: 'repeat', label: 'Repeat', render: (row: CrudRecord) => (row.repeat ? t(`parties.repeat.${String(row.repeat)}`) : '-') },
                { key: 'create_before', label: 'Before' }
              ]
            }
          ]
        : []),
      { title: 'Notes', fields: [{ key: 'details', label: 'Details' }] }
    ],
    fields: [
      { name: 'date', label: 'Date', type: 'date', required: true, defaultValue: today(), section: 'Details' },
      { name: 'title', label: 'Title', required: true, section: 'Details' },
      { name: 'amount', label: 'Amount', type: 'number', required: true, section: 'Details' },
      { name: 'reference', label: 'Reference', section: 'Details' },
      { name: 'account_id', label: 'Account', type: 'select', required: true, optionsEndpoint: 'accounts/search', section: 'Details' },
      { name: 'category_id', label: 'Category', type: 'select', required: true, optionsEndpoint: 'all_categories/search', section: 'Details' },
      ...(expense
        ? ([
            approverField({ name: 'approved_by_id', label: 'Require Approval By', section: 'Details' }),
            { name: 'recurring', label: 'Recurring expense (create automatically)', type: 'checkbox', defaultValue: false, section: 'Recurring' },
            { name: 'start_date', label: 'Start Date', type: 'date', defaultValue: today(), section: 'Recurring', visibleWhen: (form) => Boolean(form.recurring) },
            { name: 'repeat', label: 'Repeat', type: 'select', defaultValue: 'monthly', options: repeatOptions.map((o) => ({ ...o, label: t(o.label) })), section: 'Recurring', visibleWhen: (form) => Boolean(form.recurring) },
            { name: 'create_before', label: 'Create Before (days)', type: 'number', section: 'Recurring', visibleWhen: (form) => Boolean(form.recurring) }
          ] as CrudField[])
        : []),
      attachmentsField('Notes'),
      { name: 'details', label: 'Details', type: 'textarea', section: 'Notes' }
    ],
    normalizeAfterLoad: accountingAfterLoad,
    normalizeBeforeSave: (payload) => accountingBeforeSave(payload, kind),
    rowActions: expense
      ? [
          {
            key: 'approve',
            label: 'parties.approve',
            icon: TickCircle,
            color: 'success',
            confirm: 'parties.approveConfirm',
            visible: (row) => row.approved != 1,
            run: async (row, ctx) => {
              const user = asRuntimeUser(ctx.user);
              if (!isSuperUser(user) && row.user_id && user?.id && row.user_id !== user.id) {
                ctx.toast(t('parties.approveNotAllowed'), 'warning');
                return;
              }
              const response = await adminApi.post(`expenses/${row.id}/approve`, {});
              if (!response.data?.success) {
                ctx.toast(t('parties.approveNotAllowed'), 'error');
                return;
              }
              ctx.toast(t('parties.expenseApproved'), 'success');
              await ctx.reload();
            }
          }
        ]
      : base.rowActions
  };
}

// --- Accounts ---------------------------------------------------------------

const applyToOptions = [
  { value: 'in', label: 'parties.applyTo.in' },
  { value: 'out', label: 'parties.applyTo.out' },
  { value: 'both', label: 'parties.applyTo.both' }
];

function accountsConfig(base: CrudResourceConfig): CrudResourceConfig {
  return {
    ...base,
    pageSizes: [10, 25, 50, 100],
    columns: [
      { key: 'name', label: 'Name', sortable: true },
      { key: 'type', label: 'Type', sortable: true },
      { key: 'reference', label: 'Reference', sortable: true },
      { key: 'journal', label: 'Balance', align: 'right', render: journalBalanceText },
      { key: 'details', label: 'Details' },
      { key: 'offline', label: 'Offline', render: (row) => yesNoValue(row.offline == 1) },
      { key: 'fees', label: 'Fees', render: (row) => yesNoValue(row.fees == 1) },
      { key: 'fixed', label: 'Fixed', align: 'right', render: (row) => (row.fees == 1 ? moneyValue(row.fixed) : '-') },
      { key: 'percentage', label: 'Percentage', align: 'right', render: (row) => (row.fees == 1 ? `${Number(row.percentage ?? 0)}%` : '-') },
      { key: 'apply_to', label: 'Apply To', render: (row) => (row.fees == 1 && row.apply_to ? t(`parties.applyTo.${String(row.apply_to)}`) : '-') }
    ],
    detailSections: [
      {
        title: 'Account',
        fields: [
          { key: 'name', label: 'Name' },
          { key: 'type', label: 'Type' },
          { key: 'reference', label: 'Reference' },
          { key: 'opening_balance', label: 'Opening Balance', render: (row) => moneyValue(row.opening_balance) },
          { key: 'journal', label: 'Journal Balance', render: journalBalanceText },
          { key: 'offline', label: 'Offline', render: (row) => yesNoValue(row.offline == 1) }
        ]
      },
      {
        title: 'Fees',
        fields: [
          { key: 'fees', label: 'Fees', render: (row) => yesNoValue(row.fees == 1) },
          { key: 'fixed', label: 'Fixed', render: (row) => moneyValue(row.fixed) },
          { key: 'percentage', label: 'Percentage', render: (row) => `${Number(row.percentage ?? 0)}%` },
          { key: 'apply_to', label: 'Apply To', render: (row) => (row.apply_to ? t(`parties.applyTo.${String(row.apply_to)}`) : '-') }
        ]
      },
      { title: 'Notes', fields: [{ key: 'details', label: 'Details' }] }
    ],
    fields: [
      { name: 'name', label: 'Account Name', required: true, section: 'Identity' },
      { name: 'type', label: 'Type', required: true, defaultValue: 'cash', section: 'Identity' },
      { name: 'reference', label: 'Reference', section: 'Identity' },
      { name: 'details', label: 'Details', type: 'textarea', section: 'Identity' },
      // Legacy parity: the opening balance is entered on create only (it posts
      // the account's opening journal credit) and is never editable afterwards.
      { name: 'opening_balance', label: 'Opening Balance', type: 'number', required: true, defaultValue: 0, section: 'Identity', visibleWhen: (_form, ctx) => !ctx.editing },
      { name: 'offline', label: 'Offline (not usable for online payments)', type: 'checkbox', defaultValue: false, section: 'Options' },
      { name: 'fees', label: 'Charge fees on transactions', type: 'checkbox', defaultValue: false, section: 'Options' },
      { name: 'fixed', label: 'Fixed', type: 'number', section: 'Options', visibleWhen: (form) => Boolean(form.fees) },
      { name: 'percentage', label: 'Percentage', type: 'number', section: 'Options', visibleWhen: (form) => Boolean(form.fees) },
      {
        name: 'apply_to',
        label: 'Apply To',
        type: 'select',
        options: applyToOptions.map((o) => ({ ...o, label: t(o.label) })),
        section: 'Options',
        visibleWhen: (form) => Boolean(form.fees)
      }
    ],
    normalizeAfterLoad: (record) => ({ ...record, offline: record.offline == 1, fees: record.fees == 1 }),
    normalizeBeforeSave: (payload) => {
      const fees = flag(payload.fees);
      return {
        name: payload.name,
        type: payload.type || 'cash',
        reference: payload.reference ?? '',
        details: payload.details ?? '',
        offline: flag(payload.offline),
        fees,
        fixed: fees ? nullableNumber(payload.fixed) ?? 0 : 0,
        percentage: fees ? nullableNumber(payload.percentage) ?? 0 : 0,
        apply_to: fees ? payload.apply_to || 'both' : null,
        // On edit the stored value is sent back unchanged (the request requires it).
        opening_balance: Number(payload.opening_balance ?? 0)
      };
    },
    rowActions: [{ key: 'transactions', label: 'parties.transactions', icon: Book1, run: (row, ctx) => ctx.navigate(`/accounts/${row.id}/transactions`) }]
  };
}

// --- Users ------------------------------------------------------------------

const userSettingKeys = ['clock_in', 'birth_date', 'hire_date', 'address', 'number', 'salary', 'hourly_rate', 'commission_rate', 'commission_method', 'max_discount', 'first_login', 'require_password'] as const;

function metaValue(row: CrudRecord, key: string) {
  const settings = row.settings;
  if (settings && typeof settings === 'object' && !Array.isArray(settings) && key in (settings as Record<string, unknown>)) {
    return (settings as Record<string, unknown>)[key];
  }
  const meta = Array.isArray(row.meta) ? (row.meta as Array<{ meta_key?: string; meta_value?: unknown }>) : [];
  return meta.find((entry) => entry.meta_key === key)?.meta_value;
}

function metaText(key: string, money = false) {
  return (row: CrudRecord) => {
    const value = metaValue(row, key);
    if (value == null || value === '') return '-';
    return money ? moneyValue(value) : String(value);
  };
}

// Repeatable { name, file } rows submitted as files[i][name] / files[i][file].
function UserFilesField(props: { value: unknown; form: Record<string, unknown>; onChange: (value: unknown) => void }) {
  return createElement(UserFilesWidget, props);
}


// Legacy shows the user card / impersonation only when settings.impersonation == 1.
async function impersonationEnabled() {
  const settings = await fetchSettings().catch(() => null);
  const value = settings && typeof settings === 'object' ? (settings as Record<string, unknown>).impersonation : undefined;
  return value == null || String(value) === '1' || value === true;
}

function usersConfig(base: CrudResourceConfig): CrudResourceConfig {
  const baseFields = base.fields;
  const cashierFields = baseFields.filter((field) => ['cashier', 'cashier_no', 'cashier_password', 'cashier_card_no'].includes(field.name));
  const passwordFields = baseFields.filter((field) => ['password', 'password_confirmation'].includes(field.name));
  return {
    ...base,
    pageSizes: [10, 25, 50, 100],
    listFilters: [{ name: 'all', label: 'parties.allUsers', type: 'toggle', onValue: 'yes' }],
    columns: [
      { key: 'name', label: 'Name', sortable: true },
      { key: 'username', label: 'Username', sortable: true },
      { key: 'email', label: 'Email', sortable: true },
      { key: 'active', label: 'Active', render: (row) => yesNoValue(row.active == 1), tone: (row) => (row.active == 1 ? 'success' : 'error') },
      { key: 'roles', label: 'Roles', render: (row) => names(row.roles, '-') },
      { key: 'merchant', label: 'Merchant', render: (row) => relationName(row.merchant) },
      { key: 'cashier_no', label: 'Cashier No', sortable: true },
      { key: 'location', label: 'Default Location', render: (row) => relationName(row.location) },
      { key: 'locations', label: 'Locations', render: (row) => joinNames(row.locations) },
      { key: 'birth_date', label: 'Birth Date', render: metaText('birth_date') },
      { key: 'hire_date', label: 'Hire Date', render: metaText('hire_date') },
      { key: 'salary', label: 'Salary', align: 'right', render: metaText('salary', true) },
      { key: 'hourly_rate', label: 'Hourly Rate', align: 'right', render: metaText('hourly_rate', true) },
      { key: 'commission_rate', label: 'Commission Rate', align: 'right', render: metaText('commission_rate') },
      { key: 'commission_method', label: 'Commission Method', render: metaText('commission_method') },
      { key: 'clock_in', label: 'Clock In', render: (row) => {
        const value = metaValue(row, 'clock_in');
        return value ? t(`parties.clockIn.${String(value)}`) : t('parties.clockIn.disabled');
      } },
      {
        key: 'permission',
        label: 'Permissions',
        render: (row) =>
          [row.view_all == 1 ? t('parties.viewAll') : '', row.edit_all == 1 ? t('parties.editAll') : '', row.bulk_actions == 1 ? t('parties.bulkActions') : ''].filter(Boolean).join(', ') || '-'
      },
      { key: 'require_password', label: 'Require Password', render: (row) => yesNoValue(metaValue(row, 'require_password') == 1) }
    ],
    detailSections: [
      {
        title: 'Identity',
        fields: [
          { key: 'name', label: 'Name' },
          { key: 'username', label: 'Username' },
          { key: 'email', label: 'Email' },
          { key: 'phone', label: 'Phone' },
          { key: 'roles', label: 'Roles', render: (row) => names(row.roles, '-') },
          { key: 'active', label: 'Active', render: (row) => yesNoValue(row.active == 1) }
        ]
      },
      {
        title: 'Employment',
        fields: [
          { key: 'number', label: 'Employee Number', render: metaText('number') },
          { key: 'birth_date', label: 'Birth Date', render: metaText('birth_date') },
          { key: 'hire_date', label: 'Hire Date', render: metaText('hire_date') },
          { key: 'salary', label: 'Salary', render: metaText('salary', true) },
          { key: 'hourly_rate', label: 'Hourly Rate', render: metaText('hourly_rate', true) },
          { key: 'commission_rate', label: 'Commission Rate', render: metaText('commission_rate') },
          { key: 'max_discount', label: 'Max Discount', render: metaText('max_discount') },
          { key: 'address', label: 'Address', render: metaText('address') }
        ]
      },
      {
        title: 'Access',
        fields: [
          { key: 'location', label: 'Default Location', render: (row) => relationName(row.location) },
          { key: 'locations', label: 'Locations', render: (row) => joinNames(row.locations) },
          { key: 'view_all', label: 'View All', render: (row) => yesNoValue(row.view_all == 1) },
          { key: 'edit_all', label: 'Edit All', render: (row) => yesNoValue(row.edit_all == 1) },
          { key: 'bulk_actions', label: 'Bulk Actions', render: (row) => yesNoValue(row.bulk_actions == 1) },
          { key: 'can_impersonate', label: 'Can Impersonate', render: (row) => yesNoValue(row.can_impersonate == 1) }
        ]
      }
    ],
    fields: [
      { name: 'name', label: 'Full Name', required: true, section: 'Identity' },
      { name: 'phone', label: 'Phone', section: 'Identity' },
      { name: 'email', label: 'Email', section: 'Identity' },
      { name: 'username', label: 'Username', required: true, section: 'Identity', helperText: t('parties.usernameHelp') },
      { name: 'merchant_id', label: 'Merchant', type: 'select', required: true, optionsEndpoint: 'merchants/search', section: 'Assignment' },
      { name: 'location_id', label: 'Default Location', type: 'select', required: true, optionsEndpoint: 'locations/search', section: 'Assignment' },
      { name: 'locations', label: 'Locations', type: 'multiselect', optionsEndpoint: 'locations/search', defaultValue: [], section: 'Assignment' },
      {
        name: 's_clock_in',
        label: 'Clock In',
        type: 'select',
        defaultValue: '',
        options: [
          { value: 'login', label: t('parties.clockIn.login') },
          { value: 'register', label: t('parties.clockIn.register') }
        ],
        helperText: t('parties.clockInHelp'),
        section: 'Assignment'
      },
      { name: 's_number', label: 'Employee Number', section: 'Employment' },
      { name: 's_birth_date', label: 'Birth Date', type: 'date', section: 'Employment' },
      { name: 's_hire_date', label: 'Hire Date', type: 'date', section: 'Employment' },
      { name: 's_salary', label: 'Salary', type: 'number', section: 'Employment' },
      { name: 's_hourly_rate', label: 'Hourly Rate', type: 'number', section: 'Employment' },
      { name: 's_commission_rate', label: 'Commission Rate', type: 'number', section: 'Employment' },
      {
        name: 's_commission_method',
        label: 'Commission Method',
        type: 'select',
        options: [
          { value: 'sale', label: t('parties.commission.sale') },
          { value: 'profit', label: t('parties.commission.profit') }
        ],
        section: 'Employment'
      },
      { name: 's_max_discount', label: 'Max Discount', type: 'number', section: 'Employment' },
      { name: 's_address', label: 'Address', type: 'textarea', section: 'Employment' },
      ...cashierFields.map((field) => ({ ...field, section: 'Cashier' })),
      { name: 'roles', label: 'Roles', type: 'multiselect', required: true, optionsEndpoint: 'roles/search', defaultValue: [], section: 'Access' },
      { name: 'active', label: 'Active', type: 'checkbox', defaultValue: true, section: 'Access' },
      { name: 'view_all', label: 'View all records', type: 'checkbox', defaultValue: false, section: 'Access' },
      { name: 'edit_all', label: 'Edit all records', type: 'checkbox', defaultValue: false, section: 'Access' },
      { name: 'bulk_actions', label: 'Allow bulk actions', type: 'checkbox', defaultValue: false, section: 'Access' },
      { name: 's_first_login', label: 'Change password on first login', type: 'checkbox', defaultValue: false, section: 'Access' },
      { name: 'can_impersonate', label: 'Can be impersonated', type: 'checkbox', defaultValue: false, section: 'Access' },
      { name: 's_require_password', label: 'Require password (POS)', type: 'checkbox', defaultValue: false, section: 'Access' },
      {
        name: 'files',
        label: 'Files',
        type: 'custom',
        section: 'Files',
        defaultValue: [],
        render: (props) => UserFilesField(props)
      },
      ...passwordFields.map((field) => ({ ...field, section: 'Security' }))
    ],
    normalizeAfterLoad: (record) => {
      const loaded = base.normalizeAfterLoad ? base.normalizeAfterLoad(record) : record;
      const flat: Record<string, unknown> = {};
      userSettingKeys.forEach((key) => {
        const value = metaValue(record, key);
        flat[`s_${key}`] = key === 'first_login' || key === 'require_password' ? value == 1 : key.endsWith('_date') ? dateOnly(value) ?? '' : value ?? '';
      });
      return {
        ...loaded,
        ...flat,
        locations: idList(record.locations),
        location_id: record.location_id ?? '',
        merchant_id: record.merchant_id ?? '',
        active: record.active == 1,
        view_all: record.view_all == 1,
        edit_all: record.edit_all == 1,
        bulk_actions: record.bulk_actions == 1,
        can_impersonate: record.can_impersonate == 1,
        files: []
      };
    },
    normalizeBeforeSave: (payload) => {
      const result = base.normalizeBeforeSave ? base.normalizeBeforeSave(payload) : { ...payload };
      const settings: Record<string, unknown> = {};
      userSettingKeys.forEach((key) => {
        const value = payload[`s_${key}`];
        if (key === 'first_login' || key === 'require_password') settings[key] = flag(value);
        else if (['salary', 'hourly_rate', 'commission_rate', 'max_discount'].includes(key)) settings[key] = nullableNumber(value) ?? '';
        else settings[key] = value ?? '';
      });
      if (!settings.number) delete settings.number;
      const files = (Array.isArray(payload.files) ? payload.files : []).filter(
        (entry): entry is { name: string; file: File } => Boolean(entry && typeof entry === 'object' && (entry as { name?: string }).name && (entry as { file?: unknown }).file instanceof Blob)
      );
      return {
        ...result,
        merchant_id: payload.merchant_id || null,
        location_id: payload.location_id || null,
        locations: idList(payload.locations),
        view_all: flag(payload.view_all),
        edit_all: flag(payload.edit_all),
        bulk_actions: flag(payload.bulk_actions),
        can_impersonate: flag(payload.can_impersonate),
        employee: 1,
        // Always send the settings object (UserController@store/update read it).
        settings,
        ...(files.length ? { files: files.map((entry) => ({ name: entry.name, file: entry.file })) } : {})
      };
    },
    rowActions: [
      { key: 'salaries', label: 'parties.salaries', icon: Wallet, permission: 'read-salaries', run: (row, ctx) => ctx.navigate(`/salaries?user_id=${row.id}`) },
      { key: 'time_logs', label: 'parties.timeLogs', icon: Clock, permission: 'time-clock-report', run: (row, ctx) => ctx.navigate(`/reports/time-clock?user_id=${row.id}`) },
      {
        key: 'card',
        label: 'parties.userCard',
        icon: Card,
        visible: (row) => row.active == 1 && row.can_impersonate == 1,
        run: async (row, ctx) => {
          if (!(await impersonationEnabled())) {
            ctx.toast(t('parties.impersonationDisabled'), 'warning');
            return;
          }
          ctx.openDialog((close) => createElement(UserCardDialog, { user: row as never, onClose: close }), { title: t('parties.userCard'), maxWidth: 'xs' });
        }
      },
      {
        key: 'impersonate',
        label: 'parties.impersonate',
        icon: Key,
        color: 'warning',
        visible: (row) => row.active == 1 && row.can_impersonate == 1,
        confirm: (row) => t('parties.impersonateConfirm', { name: String(row.name ?? row.username ?? '') }),
        run: async (row, ctx) => {
          if (!(await impersonationEnabled())) {
            ctx.toast(t('parties.impersonationDisabled'), 'warning');
            return;
          }
          const result = await startImpersonation(String(row.username ?? ''));
          if (!result?.success) {
            ctx.toast(t('parties.cannotImpersonate'), 'error');
            return;
          }
          ctx.toast(t('parties.impersonatingAs', { name: String(result.user?.name ?? row.name ?? '') }), 'success');
          window.location.reload();
        }
      }
    ],
    headerActions: [
      {
        key: 'impersonate',
        label: 'parties.impersonate',
        icon: Key,
        variant: 'outlined',
        run: (ctx) => {
          ctx.openDialog((close) => createElement(ImpersonateDialog, { onClose: close, onDone: (message: string) => ctx.toast(message, 'success') }), { title: t('parties.impersonate'), maxWidth: 'sm' });
        }
      }
    ]
  };
}

// --- Merchants --------------------------------------------------------------

const merchantBooleans = ['active', 'print_merchant_ticket', 'allow_proforma_invoices', 'allow_offline_login', 'show_xrate', 'transfer_agent', 'allow_reprint', 'print_unit_price'];
const merchantKeys = [
  'name',
  'type',
  'phone',
  'email',
  'currency',
  'timezone',
  'ticket_header_type',
  'proforma_ticket_header_type',
  'ticket_footer_info',
  'tax_id',
  'street_no',
  'address',
  'city',
  'postal_code',
  'country',
  'state',
  'update_delay',
  'locale'
];

function merchantsConfig(base: CrudResourceConfig): CrudResourceConfig {
  return {
    ...base,
    columns: [
      { key: 'name', label: 'Name', sortable: true },
      { key: 'merchant_nr', label: 'Merchant No' },
      { key: 'email', label: 'Email' },
      { key: 'phone', label: 'Phone' },
      { key: 'city', label: 'City' },
      { key: 'country_name', label: 'Country' },
      { key: 'currency', label: 'Currency' },
      { key: 'active', label: 'Active', render: (row) => yesNoValue(row.active == 1) }
    ],
    fields: [
      { name: 'name', label: 'Name', required: true, section: 'Identity' },
      { name: 'merchant_nr', label: 'Merchant No', readOnly: true, section: 'Identity', helperText: t('parties.merchantNoHelp') },
      remoteSelect({ name: 'type', label: 'Type', required: true, endpoint: 'merchant_types', section: 'Identity' }),
      { name: 'phone', label: 'Phone', required: true, section: 'Identity' },
      { name: 'email', label: 'Email', section: 'Identity' },
      remoteSelect({ name: 'currency', label: 'Currency', required: true, endpoint: 'currencies', section: 'Config' }),
      remoteSelect({ name: 'timezone', label: 'Timezone', endpoint: 'timezones', section: 'Config' }),
      { name: 'tax_id', label: 'Tax', type: 'select', optionsEndpoint: 'taxes/search', section: 'Config' },
      { name: 'update_delay', label: 'Update Delay', type: 'number', section: 'Config' },
      { name: 'street_no', label: 'Street No', section: 'Location' },
      { name: 'address', label: 'Address', section: 'Location' },
      { name: 'city', label: 'City', section: 'Location' },
      { name: 'postal_code', label: 'Postal Code', section: 'Location' },
      countryField('Location', true),
      stateField('Location', true),
      { name: 'ticket_header_type', label: 'Ticket Header Type', required: true, section: 'Tickets' },
      { name: 'proforma_ticket_header_type', label: 'Proforma Ticket Header Type', section: 'Tickets' },
      { name: 'ticket_footer_info', label: 'Ticket Footer Info', type: 'textarea', required: true, section: 'Tickets' },
      { name: 'active', label: 'Active', type: 'checkbox', defaultValue: true, section: 'Options' },
      { name: 'print_merchant_ticket', label: 'Print merchant ticket', type: 'checkbox', section: 'Options' },
      { name: 'allow_proforma_invoices', label: 'Allow proforma invoices', type: 'checkbox', section: 'Options' },
      { name: 'allow_offline_login', label: 'Allow offline login', type: 'checkbox', section: 'Options' },
      { name: 'show_xrate', label: 'Show exchange rate', type: 'checkbox', section: 'Options' },
      { name: 'transfer_agent', label: 'Transfer agent', type: 'checkbox', section: 'Options' },
      { name: 'allow_reprint', label: 'Allow reprint', type: 'checkbox', section: 'Options' },
      { name: 'print_unit_price', label: 'Print unit price', type: 'checkbox', section: 'Options' }
    ],
    normalizeAfterLoad: (record) => ({
      ...record,
      ...merchantBooleans.reduce<Record<string, boolean>>((next, key) => ({ ...next, [key]: record[key] == 1 }), {})
    }),
    normalizeBeforeSave: (payload) => {
      const next = pick(payload, merchantKeys);
      merchantBooleans.forEach((key) => {
        next[key] = flag(payload[key]);
      });
      if (next.update_delay != null) next.update_delay = nullableNumber(next.update_delay);
      return next;
    }
  };
}

// --- Locations --------------------------------------------------------------

function locationsConfig(base: CrudResourceConfig): CrudResourceConfig {
  const registers = base.fields.filter((field) => field.name === 'registers');
  return {
    ...base,
    bulkDelete: true,
    customFields: true,
    columns: [
      { key: 'name', label: 'Name', sortable: true },
      { key: 'phone', label: 'Phone' },
      { key: 'email', label: 'Email' },
      { key: 'merchant', label: 'Merchant', render: (row) => relationName(row.merchant) },
      { key: 'account', label: 'Account', render: (row) => relationName(row.account) },
      { key: 'priceGroup', label: 'Price Group', render: (row) => relationName(row.priceGroup ?? row.price_group) },
      { key: 'address', label: 'Address', render: (row) => [row.house_no, row.street_no, row.address, row.city, row.postal_code].filter(Boolean).join(' ') || '-' },
      { key: 'state_name', label: 'State' },
      { key: 'country_name', label: 'Country' },
      { key: 'registers', label: 'Registers', render: (row) => (Array.isArray(row.registers) ? String(row.registers.length) : '-') }
    ],
    detailSections: [
      {
        title: 'Identity',
        fields: [
          { key: 'name', label: 'Name' },
          { key: 'phone', label: 'Phone' },
          { key: 'email', label: 'Email' },
          { key: 'merchant', label: 'Merchant', render: (row) => relationName(row.merchant) },
          { key: 'account', label: 'Account', render: (row) => relationName(row.account) },
          { key: 'accounts', label: 'Accounts', render: (row) => joinNames(row.accounts) },
          { key: 'color', label: 'Color' }
        ]
      },
      {
        title: 'Address',
        fields: [
          { key: 'house_no', label: 'House No' },
          { key: 'street_no', label: 'Street No' },
          { key: 'address', label: 'Address' },
          { key: 'city', label: 'City' },
          { key: 'postal_code', label: 'Postal Code' },
          { key: 'state_name', label: 'State' },
          { key: 'country_name', label: 'Country' }
        ]
      },
      { title: 'Receipt', fields: [{ key: 'header', label: 'Header' }, { key: 'footer', label: 'Footer' }] }
    ],
    fields: [
      { name: 'merchant_id', label: 'Merchant', type: 'select', optionsEndpoint: 'merchants/search', section: 'Identity' },
      { name: 'name', label: 'Location Name', required: true, section: 'Identity' },
      { name: 'phone', label: 'Phone', required: true, section: 'Identity' },
      { name: 'email', label: 'Email', required: true, section: 'Identity' },
      { name: 'account_id', label: 'Default Account', type: 'select', required: true, optionsEndpoint: 'accounts/search', section: 'Configuration', helperText: t('parties.defaultAccountHelp') },
      { name: 'accounts', label: 'Accounts', type: 'multiselect', optionsEndpoint: 'accounts/search', defaultValue: [], section: 'Configuration' },
      { name: 'price_group_id', label: 'Price Group', type: 'select', optionsEndpoint: 'price_groups/search', section: 'Configuration' },
      { name: 'color', label: 'Color', type: 'color', section: 'Configuration' },
      { name: 'logo', label: 'Logo', type: 'file', accept: '.jpg,.jpeg,.png,.svg', helperText: t('parties.logoHelp'), section: 'Configuration' },
      ...addressFields('Address'),
      { name: 'header', label: 'Receipt Header', type: 'textarea', section: 'Receipt' },
      { name: 'footer', label: 'Receipt Footer', type: 'textarea', section: 'Receipt' },
      ...registers
    ],
    normalizeAfterLoad: (record) => {
      const loaded = base.normalizeAfterLoad ? base.normalizeAfterLoad(record) : record;
      return { ...loaded, accounts: idList(record.accounts), country: record.country ?? '', state: record.state ?? '', color: record.color ?? '' };
    },
    normalizeBeforeSave: (payload) => {
      const next = base.normalizeBeforeSave ? base.normalizeBeforeSave(payload) : { ...payload };
      const clean = pick(next, [
        'merchant_id',
        'name',
        'phone',
        'email',
        'account_id',
        'price_group_id',
        'color',
        'logo',
        'house_no',
        'street_no',
        'address',
        'city',
        'postal_code',
        'country',
        'state',
        'header',
        'footer',
        'registers'
      ]);
      return { ...clean, accounts: idList(payload.accounts) };
    }
  };
}

// --- Salaries ---------------------------------------------------------------

function salariesConfig(base: CrudResourceConfig): CrudResourceConfig {
  return {
    ...base,
    showAttachments: true,
    listFilters: [{ name: 'user_id', label: 'Employee', type: 'select', optionsEndpoint: 'users/search' }],
    fields: [...base.fields.filter((field) => field.name !== 'details'), attachmentsField('Notes'), ...base.fields.filter((field) => field.name === 'details')],
    normalizeAfterLoad: (record) => ({ ...(base.normalizeAfterLoad ? base.normalizeAfterLoad(record) : record), date: dateOnly(record.date) ?? today(), attachments: [] }),
    normalizeBeforeSave: (payload) => ({ ...(base.normalizeBeforeSave ? base.normalizeBeforeSave(payload) : payload), attachments: payload.attachments }),
    headerActions: [
      {
        key: 'generate',
        label: 'parties.generateSalaries',
        icon: DocumentText,
        permission: 'create-salaries',
        confirm: 'parties.generateSalariesConfirm',
        run: async (ctx) => {
          const response = await adminApi.post('salaries/generate', {});
          ctx.toast(String(response.data?.message || t('parties.salariesGenerated')).trim() || t('parties.salariesGenerated'), response.data?.success ? 'success' : 'error');
          await ctx.reload();
        }
      }
    ]
  };
}

// --- Halls / tables & reference data ---------------------------------------

function hallsConfig(base: CrudResourceConfig): CrudResourceConfig {
  return {
    ...base,
    bulkDelete: true,
    customFields: true,
    columns: [...base.columns, { key: 'extra_attributes', label: 'Custom Fields', render: extrasSummary }],
    rowActions: [
      { key: 'floor', label: 'floor.open', icon: Map1, permission: 'read-tables', run: (row, ctx) => ctx.navigate(`/halls/${row.id}/floor`) },
      { key: 'tables', label: 'parties.listTables', icon: Book1, permission: 'read-tables', run: (row, ctx) => ctx.navigate(`/tables?hall=${row.id}`) },
      { key: 'add_table', label: 'parties.addTable', icon: UserAdd, permission: 'create-tables', run: (row, ctx) => ctx.navigate(`/tables?create=1&hall_id=${row.id}`) }
    ]
  };
}

function tablesConfig(base: CrudResourceConfig): CrudResourceConfig {
  return {
    ...base,
    bulkDelete: true,
    customFields: true,
    listFilters: [{ name: 'hall', label: 'Hall', type: 'select', optionsEndpoint: 'halls/search' }],
    columns: [...base.columns, { key: 'extra_attributes', label: 'Custom Fields', render: extrasSummary }]
  };
}

const optionTypes = ['select', 'checkbox', 'radio'];

function withPhoto(base: CrudResourceConfig): CrudResourceConfig {
  return {
    ...base,
    bulkDelete: true,
    fields: [...base.fields, photoField],
    columns: [{ key: 'photo', label: 'Photo', render: (row) => (row.photo ? '🖼' : '-'), align: 'center' }, ...base.columns]
  };
}

export const overrides: Record<string, Override> = {
  customers: (base) => partyConfig(need(base, 'customers'), 'customers'),
  suppliers: (base) => partyConfig(need(base, 'suppliers'), 'suppliers'),
  accounts: (base) => accountsConfig(need(base, 'accounts')),
  incomes: (base) => accountingConfig(need(base, 'incomes'), 'income'),
  expenses: (base) => accountingConfig(need(base, 'expenses'), 'expense'),
  users: (base) => usersConfig(need(base, 'users')),
  roles: (base) => {
    const config = need(base, 'roles');
    return {
      ...config,
      fields: config.fields.map((field) => (field.name === 'name' ? { ...field, helperText: t('parties.roleNameHelp') } : field)),
      rowActions: [{ key: 'permissions', label: 'parties.permissions', icon: Key, permission: 'update-roles', run: (row, ctx) => ctx.navigate(`/roles/${row.id}/permissions`) }]
    };
  },
  merchants: (base) => merchantsConfig(need(base, 'merchants')),
  locations: (base) => locationsConfig(need(base, 'locations')),
  salaries: (base) => salariesConfig(need(base, 'salaries')),
  halls: (base) => hallsConfig(need(base, 'halls')),
  tables: (base) => tablesConfig(need(base, 'tables')),
  brands: (base) => withPhoto(need(base, 'brands')),
  categories: (base) => withPhoto(need(base, 'categories')),
  units: (base) => {
    const config = need(base, 'units');
    return {
      ...config,
      bulkDelete: true,
      fields: config.fields.map((field) => (field.name === 'operator' || field.name === 'operation_value' ? { ...field, visibleWhen: (form: Record<string, unknown>) => Boolean(form.base_id) } : field)),
      normalizeBeforeSave: (payload) => {
        const next = config.normalizeBeforeSave ? config.normalizeBeforeSave(payload) : { ...payload };
        if (!payload.base_id) return { ...next, base_id: null, operator: null, operation_value: null };
        return next;
      }
    };
  },
  taxes: (base) => {
    const config = need(base, 'taxes');
    return {
      ...config,
      bulkDelete: true,
      fields: config.fields.map((field) => (field.name === 'same' ? { ...field, type: 'checkbox', options: undefined, visibleWhen: (form: Record<string, unknown>) => Boolean(form.state) } : field)),
      normalizeBeforeSave: (payload) => {
        const next = config.normalizeBeforeSave ? config.normalizeBeforeSave(payload) : { ...payload };
        return payload.state ? next : { ...next, same: 0 };
      }
    };
  },
  fields: (base) => {
    const config = need(base, 'fields');
    return {
      ...config,
      bulkDelete: true,
      fields: config.fields.map((field) => (field.name === 'options' ? { ...field, visibleWhen: (form: Record<string, unknown>) => optionTypes.includes(String(form.type ?? '')) } : field)),
      normalizeBeforeSave: (payload) => {
        const next = config.normalizeBeforeSave ? config.normalizeBeforeSave(payload) : { ...payload };
        return optionTypes.includes(String(payload.type ?? '')) ? next : { ...next, options: null };
      }
    };
  },
  customer_groups: (base) => ({ ...need(base, 'customer_groups'), bulkDelete: true }),
  // P14: modifiers keep whatever the catalog override defines; only add custom fields.
  modifiers: (base) => ({ ...need(base, 'modifiers'), customFields: true })
};
